'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ReferralStats {
  referralCode: string;
  totalReferrals: number;
  premiumDaysEarned: number;
  premiumExpiresAt: string | null;
  cosmeticBadge: string | null;
  recentReferrals: { createdAt: string; rewardDays: number; cosmeticUnlocked: string | null }[];
}

const COSMETIC_LABELS: Record<string, { label: string; emoji: string; color: string }> = {
  spark_badge: { label: 'Spark Badge', emoji: '✨', color: '#ffd23f' },
  flame_badge: { label: 'Flame Badge', emoji: '🔥', color: '#ff9500' },
  crown_silver: { label: 'Silver Crown', emoji: '👑', color: '#94a3b8' },
  crown_gold: { label: 'Gold Crown', emoji: '👑', color: '#ffd23f' },
};

const MILESTONES = [
  { count: 1, reward: '7 free premium days', cosmetic: 'spark_badge', emoji: '✨', label: 'First Invite' },
  { count: 2, reward: '14 free premium days', cosmetic: 'flame_badge', emoji: '🔥', label: 'Flame Badge' },
  { count: 5, reward: '35 free premium days', cosmetic: 'crown_silver', emoji: '👑', label: 'Silver Crown' },
  { count: 10, reward: '70 free premium days', cosmetic: 'crown_gold', emoji: '👑', label: 'Gold Crown' },
];

// Milestone toast component
interface MilestoneToastProps {
  milestone: typeof MILESTONES[0] | null;
  onDismiss: () => void;
}

function MilestoneToast({ milestone, onDismiss }: MilestoneToastProps) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!milestone) return;
    setExiting(false);
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onDismiss, 300);
    }, 4000);
    return () => clearTimeout(timer);
  }, [milestone, onDismiss]);

  if (!milestone) return null;

  return (
    <div
      className={`fixed bottom-6 left-1/2 z-50 px-5 py-4 rounded-2xl shadow-2xl flex items-center gap-3 ${exiting ? 'toast-exit' : 'toast-enter'}`}
      style={{
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, rgba(22,22,42,0.98), rgba(15,15,30,0.99))',
        border: '1px solid rgba(255,210,63,0.5)',
        boxShadow: '0 0 40px rgba(255,210,63,0.2)',
        minWidth: '280px',
        maxWidth: '360px',
      }}
      role="status"
      aria-live="polite"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
        style={{ background: 'rgba(255,210,63,0.15)', border: '1px solid rgba(255,210,63,0.35)' }}
      >
        {milestone.emoji}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-black text-sm text-white leading-tight">Milestone Unlocked! 🎉</p>
        <p className="text-xs font-semibold mt-0.5" style={{ color: '#ffd23f' }}>
          {milestone.label} · {milestone.reward}
        </p>
      </div>
      <button
        onClick={() => { setExiting(true); setTimeout(onDismiss, 300); }}
        className="flex-shrink-0 text-xs font-bold opacity-50 hover:opacity-100 transition-opacity"
        style={{ color: 'rgba(255,255,255,0.6)' }}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

export default function ReferralPanel() {
  const { user } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState<{ success: boolean; message: string } | null>(null);
  const [milestoneToast, setMilestoneToast] = useState<typeof MILESTONES[0] | null>(null);
  const prevReferralCount = useRef<number | null>(null);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://unbored3102.builtwithrocket.new';

  const checkMilestoneToast = useCallback((prevCount: number | null, newCount: number) => {
    if (prevCount === null) return; // first load, don't toast
    // Find milestones crossed between prevCount and newCount
    const crossed = MILESTONES.filter(m => m.count > (prevCount ?? 0) && m.count <= newCount);
    if (crossed.length > 0) {
      // Show the highest milestone crossed
      setMilestoneToast(crossed[crossed.length - 1]);
    }
  }, []);

  const loadStats = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const supabase = createClient();
    try {
      const { data: codeData } = await supabase.rpc('ensure_referral_code', { p_user_id: user.id });
      const referralCode = codeData as string;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('referral_code, premium_days_earned, premium_expires_at, cosmetic_badge')
        .eq('id', user.id)
        .single();

      const { data: referrals } = await supabase
        .from('referrals')
        .select('created_at, reward_days, cosmetic_unlocked')
        .eq('referrer_id', user.id)
        .eq('status', 'rewarded')
        .order('created_at', { ascending: false })
        .limit(5);

      const newCount = referrals?.length || 0;
      checkMilestoneToast(prevReferralCount.current, newCount);
      prevReferralCount.current = newCount;

      setStats({
        referralCode: profile?.referral_code || referralCode || '',
        totalReferrals: newCount,
        premiumDaysEarned: profile?.premium_days_earned || 0,
        premiumExpiresAt: profile?.premium_expires_at || null,
        cosmeticBadge: profile?.cosmetic_badge || null,
        recentReferrals: (referrals || []).map(r => ({
          createdAt: r.created_at,
          rewardDays: r.reward_days,
          cosmeticUnlocked: r.cosmetic_unlocked,
        })),
      });
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [user, checkMilestoneToast]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Real-time subscription to referrals table to detect new referrals
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    const channel = supabase
      .channel(`referrals-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'referrals', filter: `referrer_id=eq.${user.id}` },
        () => { loadStats(); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, loadStats]);

  const inviteLink = stats?.referralCode
    ? `${siteUrl}/?ref=${stats.referralCode}`
    : '';

  const handleCopy = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleShare = async () => {
    if (!inviteLink) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'Join Unbored — Free Premium Days',
          text: '🎮 I\'m playing Unbored — micro-games engineered to rewire your brain. Use my invite link and we both get free premium days!',
          url: inviteLink,
        });
      } catch {
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const handleApplyCode = async () => {
    if (!user || !applyCode.trim()) return;
    setApplyLoading(true);
    setApplyResult(null);
    const supabase = createClient();
    try {
      const { data, error } = await supabase.rpc('process_referral', {
        p_referred_id: user.id,
        p_referral_code: applyCode.trim().toUpperCase(),
      });
      if (error) {
        setApplyResult({ success: false, message: error.message });
      } else {
        const result = data as { success: boolean; error?: string; reward_days?: number; cosmetic?: string };
        if (result.success) {
          setApplyResult({ success: true, message: `🎉 Code applied! Your referrer earned ${result.reward_days} free premium days.` });
          setApplyCode('');
          loadStats();
        } else {
          setApplyResult({ success: false, message: result.error || 'Invalid code' });
        }
      }
    } catch {
      setApplyResult({ success: false, message: 'Something went wrong. Try again.' });
    } finally {
      setApplyLoading(false);
    }
  };

  const nextMilestone = MILESTONES.find(m => (stats?.totalReferrals || 0) < m.count);
  const progressToNext = nextMilestone
    ? Math.min(((stats?.totalReferrals || 0) / nextMilestone.count) * 100, 100)
    : 100;

  if (!user) {
    return (
      <div
        className="w-full rounded-2xl p-6 text-center"
        style={{
          background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
          border: '1px solid rgba(42,42,69,0.8)',
        }}
      >
        <div className="text-3xl mb-3">🔗</div>
        <p className="text-sm text-muted-foreground font-medium">Sign in to get your invite link and earn free premium days.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="w-full rounded-2xl p-6 flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
          border: '1px solid rgba(42,42,69,0.8)',
          minHeight: 120,
        }}
      >
        <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'rgba(255,210,63,0.6)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <>
      <MilestoneToast milestone={milestoneToast} onDismiss={() => setMilestoneToast(null)} />
      <div className="w-full flex flex-col gap-4">
        {/* Header card */}
        <div
          className="w-full rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(22,22,42,0.95), rgba(15,15,30,0.98))',
            border: '1px solid rgba(255,210,63,0.25)',
            boxShadow: '0 0 30px rgba(255,210,63,0.06)',
          }}
        >
          {/* Gold accent bar */}
          <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, #ffd23f 0%, #ff9500 50%, #ff3b6b 100%)' }} />

          <div className="p-5 flex flex-col gap-4">
            {/* Title row */}
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: 'rgba(255,210,63,0.12)', border: '1px solid rgba(255,210,63,0.3)' }}
              >
                🔗
              </div>
              <div>
                <h3 className="font-black text-foreground text-base leading-tight">Invite & Earn</h3>
                <p className="text-xs text-muted-foreground font-medium">Share your link → earn 7 free premium days per friend</p>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: stats?.totalReferrals || 0, label: 'Friends Invited' },
                { value: stats?.premiumDaysEarned || 0, label: 'Days Earned' },
                {
                  value: stats?.cosmeticBadge
                    ? (COSMETIC_LABELS[stats.cosmeticBadge]?.emoji || '🎖️')
                    : '—',
                  label: 'Cosmetic',
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl p-3 text-center"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <div
                    className="font-black text-xl leading-tight"
                    style={{
                      background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {s.value}
                  </div>
                  <div className="text-xs text-muted-foreground font-medium mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Invite link box */}
            <div>
              <p className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wider">Your Invite Link</p>
              <div
                className="flex items-center gap-2 rounded-xl px-3 py-2.5"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <span className="flex-1 text-xs font-mono text-foreground truncate" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {inviteLink || 'Generating…'}
                </span>
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold transition-all"
                  style={{
                    background: copied ? 'rgba(54,226,164,0.15)' : 'rgba(255,210,63,0.12)',
                    border: `1px solid ${copied ? 'rgba(54,226,164,0.4)' : 'rgba(255,210,63,0.3)'}`,
                    color: copied ? '#36e2a4' : '#ffd23f',
                  }}
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Share button */}
            <button
              onClick={handleShare}
              className="w-full py-3 rounded-xl font-black text-sm tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 100%)',
                color: '#0a0a0f',
                border: 'none',
                boxShadow: '0 0 24px rgba(255,210,63,0.25)',
              }}
            >
              🚀 Share Invite Link
            </button>
          </div>
        </div>

        {/* Milestone progress */}
        <div
          className="w-full rounded-2xl p-5"
          style={{
            background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
            border: '1px solid rgba(42,42,69,0.8)',
          }}
        >
          <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-4">Reward Milestones</p>
          <div className="flex flex-col gap-3">
            {MILESTONES.map((m) => {
              const reached = (stats?.totalReferrals || 0) >= m.count;
              return (
                <div key={m.count} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-base flex-shrink-0"
                    style={{
                      background: reached ? 'rgba(255,210,63,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${reached ? 'rgba(255,210,63,0.5)' : 'rgba(255,255,255,0.08)'}`,
                    }}
                  >
                    {reached ? '✓' : m.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-foreground">{m.count} {m.count === 1 ? 'friend' : 'friends'}</span>
                      <span
                        className="text-xs px-1.5 py-0.5 rounded font-bold"
                        style={{
                          background: reached ? 'rgba(54,226,164,0.12)' : 'rgba(255,255,255,0.05)',
                          color: reached ? '#36e2a4' : 'rgba(255,255,255,0.3)',
                          fontSize: '0.6rem',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {reached ? 'UNLOCKED' : 'LOCKED'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{m.reward} · {m.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar to next milestone */}
          {nextMilestone && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-muted-foreground font-medium">
                  {stats?.totalReferrals || 0} / {nextMilestone.count} to next milestone
                </span>
                <span className="text-xs font-bold" style={{ color: '#ffd23f' }}>{Math.round(progressToNext)}%</span>
              </div>
              <div className="w-full h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${progressToNext}%`,
                    background: 'linear-gradient(90deg, #ffd23f 0%, #ff9500 100%)',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Apply a referral code (for new users) */}
        <div
          className="w-full rounded-2xl p-5"
          style={{
            background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
            border: '1px solid rgba(42,42,69,0.8)',
          }}
        >
          <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Got a Friend's Code?</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={applyCode}
              onChange={e => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter invite code…"
              maxLength={12}
              className="flex-1 rounded-xl px-3 py-2.5 text-sm font-mono font-bold outline-none"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
              }}
            />
            <button
              onClick={handleApplyCode}
              disabled={applyLoading || !applyCode.trim()}
              className="px-4 py-2.5 rounded-xl text-sm font-black transition-all"
              style={{
                background: applyLoading || !applyCode.trim()
                  ? 'rgba(255,255,255,0.06)'
                  : 'linear-gradient(135deg, #ffd23f 0%, #ff9500 100%)',
                color: applyLoading || !applyCode.trim() ? 'rgba(255,255,255,0.3)' : '#0a0a0f',
                border: 'none',
                cursor: applyLoading || !applyCode.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {applyLoading ? '…' : 'Apply'}
            </button>
          </div>
          {applyResult && (
            <p
              className="mt-2 text-xs font-semibold"
              style={{ color: applyResult.success ? '#36e2a4' : '#ff3b6b' }}
            >
              {applyResult.message}
            </p>
          )}
        </div>

        {/* Recent referrals */}
        {(stats?.recentReferrals?.length || 0) > 0 && (
          <div
            className="w-full rounded-2xl p-5"
            style={{
              background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
              border: '1px solid rgba(42,42,69,0.8)',
            }}
          >
            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Recent Rewards</p>
            <div className="flex flex-col gap-2">
              {stats?.recentReferrals?.map((r, i) => {
                const cosmetic = r.cosmeticUnlocked ? COSMETIC_LABELS[r.cosmeticUnlocked] : null;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-base">{cosmetic?.emoji || '🎁'}</span>
                    <div className="flex-1">
                      <span className="text-xs font-bold text-foreground">+{r.rewardDays} premium days</span>
                      {cosmetic && (
                        <span className="ml-2 text-xs font-semibold" style={{ color: cosmetic.color }}>
                          · {cosmetic.label} unlocked
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
